import { AuthApi } from './authApi';
import { AuthTokens } from '../types/auth.types';

export interface PollingCallbacks {
  onSuccess: (tokens: AuthTokens) => void;
  onError: (error: string) => void;
  onProgress?: (message: string) => void;
}

export interface PollingOptions {
  maxRetries?: number; // Default: 180 (15 minutes at 5-second intervals)
  maxFailures?: number; // Default: 5 consecutive failures before giving up
}

export class PollingService {
  private pollInterval: NodeJS.Timeout | null = null;
  private retryCount = 0;
  private maxRetries: number;
  private failureCount = 0;
  private maxFailures: number;
  private isPolling = false;

  constructor(options: PollingOptions = {}) {
    this.maxRetries = options.maxRetries ?? 180; // 15 minutes at 5-second intervals
    this.maxFailures = options.maxFailures ?? 5; // Stop after 5 consecutive failures
  }

  async startPolling(
    deviceCode: string,
    interval: number,
    callbacks: PollingCallbacks
  ): Promise<void> {
    if (this.isPolling) {
      return;
    }

    this.isPolling = true;
    this.retryCount = 0;
    this.failureCount = 0;

    callbacks.onProgress?.('Waiting for authentication...');

    this.pollInterval = setInterval(async () => {
      try {
        const response = await AuthApi.checkDeviceStatus(deviceCode);
        this.failureCount = 0; // Reset failure count on successful API call

        if (response.status === 'completed') {
          this.stopPolling();
          
          if (response.access_token) {
            const tokens: AuthTokens = {
              accessToken: response.access_token,
              refreshToken: response.refresh_token || '',
              idToken: response.id_token || '',
              expiresIn: response.expires_in || 3600,
              tokenType: response.token_type || 'Bearer'
            };
            
            callbacks.onSuccess(tokens);
          } else {
            callbacks.onError('Authentication completed but no tokens received');
          }
          return;
        }

        if (response.status === 'expired' || response.status === 'invalid') {
          this.stopPolling();
          callbacks.onError(response.message || `Authentication ${response.status}`);
          return;
        }

        // Continue polling for 'authorization_pending'
        this.retryCount++;
        
        if (this.retryCount >= this.maxRetries) {
          this.stopPolling();
          callbacks.onError('Authentication timeout - please try again');
          return;
        }

        // Update progress message with remaining time
        const remainingTime = Math.max(0, (this.maxRetries - this.retryCount) * interval);
        const minutes = Math.floor(remainingTime / 60);
        const seconds = remainingTime % 60;
        callbacks.onProgress?.(
          `Waiting for authentication... (${minutes}:${seconds.toString().padStart(2, '0')} remaining)`
        );

      } catch (error) {
        this.failureCount++;

        if (this.failureCount >= this.maxFailures) {
          this.stopPolling();
          callbacks.onError('Network error - please check your connection and try again');
          return;
        }

        // Continue polling - might be temporary network issue
        callbacks.onProgress?.('Network issue - retrying...');
      }
    }, interval * 1000);
  }

  stopPolling(): void {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = null;
    }
    this.isPolling = false;
    this.retryCount = 0;
    this.failureCount = 0;
  }

  isActive(): boolean {
    return this.isPolling;
  }

  getRetryCount(): number {
    return this.retryCount;
  }

  getRemainingRetries(): number {
    return Math.max(0, this.maxRetries - this.retryCount);
  }
}

// Export singleton instance
export const pollingService = new PollingService();